import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Pagination,
  Paper,
  TextField,
} from '@mui/material';
import { useCallback, useState } from 'react';
import type { VolumeBatchHistoryItem } from '../../../api/src/fakes/volume-batch';
import {
  GOOGLE_VOLUME_LANGUAGES,
  type QueryVolumeRefresh,
} from '../../../api/src/fakes/query-volume';
import { api } from '../api/client';
import { Stack, Typography } from '../components/mui-system';
import { useVisiblePolling } from '../hooks/useVisiblePolling';
import { FakesVolumeStopButton } from './FakesVolumeStopButton';

const statuses: Record<VolumeBatchHistoryItem['status'], string> = {
  queued: 'В очереди',
  running: 'Выполняется',
  stopping: 'Останавливается',
  stopped: 'Остановлен пользователем',
  paused: 'Приостановлен',
  complete: 'Завершён',
  error: 'Завершён с ошибками',
};
const attemptStatuses: Record<QueryVolumeRefresh['status'], string> = {
  pending: 'В очереди',
  processing: 'Выполняется',
  complete: 'Собрано',
  error: 'Ошибка',
  paused: 'Не отправлено',
};
const date = (value: string) => new Date(value).toLocaleString('ru-RU');
function BatchSummary({ batch }: { batch: VolumeBatchHistoryItem }) {
  const ctx = batch.context;
  return (
    <Stack gap={0.5}>
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        <Typography fontWeight={600}>
          {ctx.source === 'wordstat' ? 'Яндекс · Wordstat' : 'Google · Keyword Planner'}
        </Typography>
        <Chip size="small" label={statuses[batch.status]} />
      </Stack>
      <Typography variant="body2">
        {ctx.regionName} ·{' '}
        {ctx.source === 'wordstat'
          ? `${ctx.regionCode} · ${ctx.device === 'mobile' ? 'телефоны' : 'компьютеры'}`
          : `${ctx.geoTargetId} · ${GOOGLE_VOLUME_LANGUAGES[ctx.languageId]} · все устройства`}
      </Typography>
      <Typography variant="body2">
        {date(batch.createdAt)} · {batch.scheduledFor ? 'По расписанию' : 'Вручную'} ·{' '}
        {batch.retryFailed ? 'Повтор ошибок' : batch.onlyMissing ? 'Недостающие' : 'Весь список'}
      </Typography>
      <Typography variant="body2">
        Собрано {batch.complete} из {batch.total} · ошибок {batch.failed} · в работе {batch.pending}{' '}
        · не отправлено {batch.paused}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Обращений за частотностью: {batch.requests} · начато {batch.startedRequests} · выполняется
        фраз: {batch.processing}
      </Typography>
      {batch.stop && (
        <Alert severity={batch.stop.kind === 'user' ? 'info' : 'warning'}>
          {batch.stop.message} {date(batch.stop.stoppedAt)}.
          {batch.pending > 0 &&
            ' Ожидаем результат начатого обращения; новые обращения не запускаются.'}
          {batch.stop.retryAt && ` Повтор не раньше ${date(batch.stop.retryAt)}.`}
        </Alert>
      )}
    </Stack>
  );
}
function BatchDetails({
  workspaceId,
  batchId,
  editable,
  onChanged,
}: {
  workspaceId: string;
  batchId: string;
  editable: boolean;
  onChanged(): void;
}) {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<QueryVolumeRefresh['status'] | 'all'>('all');
  const load = useCallback(
    () => api.fakesVolumeDetails(workspaceId, batchId, page, status === 'all' ? undefined : status),
    [workspaceId, batchId, page, status],
  );
  const details = useVisiblePolling(load, 5000);
  const data = details.data;
  return (
    <Stack gap={2}>
      {details.error && (
        <Alert severity="error">
          {details.error}
          <Button onClick={details.refresh}>Повторить загрузку</Button>
        </Alert>
      )}
      {!data && !details.error && <LinearProgress />}
      {data && (
        <>
          <BatchSummary batch={data.batch} />
          <Typography variant="caption" color="text.secondary">
            Пакет {data.batch.id}
            <br />
            Начало обращений:{' '}
            {data.batch.startedAt ? date(data.batch.startedAt) : 'не зафиксировано'} · завершение:{' '}
            {data.batch.completedAt
              ? date(data.batch.completedAt)
              : data.batch.pending
                ? 'ещё не завершён'
                : 'не зафиксировано для старого пакета'}
            {data.batch.scheduledFor && (
              <>
                <br />
                Плановый срок: {date(data.batch.scheduledFor)}
              </>
            )}
          </Typography>
          {editable && (
            <FakesVolumeStopButton
              workspaceId={workspaceId}
              batch={data.batch}
              onStopped={() => {
                details.refresh();
                onChanged();
              }}
            />
          )}
        </>
      )}
      <TextField
        select
        label="Статус фраз"
        value={status}
        onChange={(event) => {
          setStatus(event.target.value as typeof status);
          setPage(1);
        }}
      >
        <MenuItem value="all">Все фразы</MenuItem>
        {Object.entries(attemptStatuses).map(([value, label]) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
      </TextField>
      {data?.items.map((item) => (
        <Paper key={item.id} variant="outlined" sx={{ p: 1.5 }}>
          <Typography fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
            {item.query}
          </Typography>
          <Typography variant="body2">
            {attemptStatuses[item.status]}
            {item.value !== null
              ? ` · ${item.value.toLocaleString('ru-RU')}${data.batch.context.source === 'wordstat' ? ' / 30 дней' : ' / мес.'}`
              : ' · значение не получено'}
          </Typography>
          {item.phrase && data.batch.context.source === 'wordstat' && (
            <Typography variant="caption" sx={{ overflowWrap: 'anywhere' }}>
              Фраза Wordstat: {item.phrase}
            </Typography>
          )}
          {item.error && (
            <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
              {item.error}
            </Typography>
          )}
          {item.capturedAt && (
            <Typography variant="caption">Измерено: {date(item.capturedAt)}</Typography>
          )}
        </Paper>
      ))}
      {data && !data.total && (
        <Typography color="text.secondary">Фраз с выбранным статусом нет.</Typography>
      )}
      {data && data.total > data.pageSize && (
        <Pagination
          aria-label="Страницы фраз"
          page={page}
          count={Math.ceil(data.total / data.pageSize)}
          onChange={(_, value) => setPage(value)}
        />
      )}
    </Stack>
  );
}
export function FakesVolumeHistoryDialog({
  workspaceId,
  editable,
  onChanged,
  onClose,
}: {
  workspaceId: string;
  editable: boolean;
  onChanged(): void;
  onClose(): void;
}) {
  const [cursors, setCursors] = useState<Array<string | undefined>>([undefined]);
  const before = cursors.at(-1);
  const [selected, setSelected] = useState<string | null>(null);
  const load = useCallback(
    () => api.fakesVolumeHistory(workspaceId, before),
    [workspaceId, before],
  );
  const history = useVisiblePolling(load, 5000);
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{selected ? 'Пакет частотности' : 'История пакетов частотности'}</DialogTitle>
      <DialogContent>
        <Stack gap={2} pt={1}>
          <Typography variant="body2" color="text.secondary">
            Запуски обоих источников во всех контекстах. Время — по часовому поясу браузера. Число
            начатых обращений учитывает объединение фраз; это не счёт провайдера. Проверка региона и
            авторизация Google в него не входят.
          </Typography>
          {selected ? (
            <BatchDetails
              key={selected}
              workspaceId={workspaceId}
              batchId={selected}
              editable={editable}
              onChanged={() => {
                history.refresh();
                onChanged();
              }}
            />
          ) : (
            <>
              {history.error && (
                <Alert severity="error">
                  {history.error}
                  <Button onClick={history.refresh}>Повторить загрузку</Button>
                </Alert>
              )}
              {!history.data && !history.error && <LinearProgress />}
              {history.data?.items.map((batch) => (
                <Paper key={batch.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack gap={1}>
                    <BatchSummary batch={batch} />
                    <Stack direction="row" gap={1}>
                      <Button onClick={() => setSelected(batch.id)}>Фразы и ошибки</Button>
                    </Stack>
                    {editable && (
                      <FakesVolumeStopButton
                        workspaceId={workspaceId}
                        batch={batch}
                        onStopped={() => {
                          history.refresh();
                          onChanged();
                        }}
                      />
                    )}
                  </Stack>
                </Paper>
              ))}
              {history.data && !history.data.items.length && (
                <Typography>Сбор частотности ещё не запускался.</Typography>
              )}
              <Stack direction="row" gap={1}>
                <Button
                  disabled={cursors.length === 1}
                  onClick={() => setCursors((value) => value.slice(0, -1))}
                >
                  Новые пакеты
                </Button>
                <Button
                  disabled={!history.data?.nextCursor}
                  onClick={() => setCursors((value) => [...value, history.data!.nextCursor!])}
                >
                  Старые пакеты
                </Button>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {selected && <Button onClick={() => setSelected(null)}>К истории</Button>}
        <Button onClick={onClose}>Закрыть историю</Button>
      </DialogActions>
    </Dialog>
  );
}
